import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { BucketItem, Location } from "../types";
import { STORAGE_KEYS } from "../constants";
import { LANDMARKS } from "../data/landmarks";
import { BucketItemSchema } from "../types/schemas";
import { analyticsEvents } from "../hooks/useAnalytics";

interface BucketListState {
  items: BucketItem[];
  landmarks: Location[];
  setItems: (items: BucketItem[]) => void;
  addItem: (item: BucketItem) => void;
  updateItem: (id: string, item: Partial<BucketItem>) => void;
  deleteItem: (id: string) => void;
  toggleComplete: (id: string) => void;
  getItemById: (id: string) => BucketItem | undefined;
  addCustomLocation: (location: Location) => void;
}

/**
 * Zustand store for managing bucket list state
 * Persists data to localStorage automatically
 */
export const useBucketListStore = create<BucketListState>()(
  persist(
    (set, get) => ({
      items: [],
      landmarks: LANDMARKS,

      setItems: (items) => {
        set({ items });
      },

      addItem: (item) => {
        try {
          const validatedItem = BucketItemSchema.parse(item);
          // Ensure ownerId is always set (safety check)
          if (!validatedItem.ownerId) {
            console.warn('Item added without ownerId:', validatedItem.id);
            validatedItem.ownerId = '';
          }
          set((state) => ({
            items: [...state.items, validatedItem],
          }));
          analyticsEvents.addItem(validatedItem.category);
        } catch (error) {
          console.error('Validation error when adding item:', error);
          throw error;
        }
      },

      updateItem: (id, updates) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? ({ ...item, ...updates } as typeof item) : item,
          ),
        }));
      },

      deleteItem: (id) => {
        const item = get().items.find((item) => item.id === id);
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }));
        if (item) {
          analyticsEvents.deleteItem(id, item.category);
        }
      },

      toggleComplete: (id) => {
        const item = get().items.find((item) => item.id === id);
        const willBeCompleted = item ? !item.completed : false;
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id
              ? ({
                  ...item,
                  completed: !item.completed,
                  completionDate: !item.completed
                    ? new Date().toISOString()
                    : undefined,
                } as typeof item)
              : item,
          ),
        }));
        analyticsEvents.toggleComplete(id, willBeCompleted);
      },

      getItemById: (id) => {
        return get().items.find((item) => item.id === id);
      },

      addCustomLocation: (location) => {
        set((state) => ({
          landmarks: [...state.landmarks, location],
        }));
      },
    }),
    {
      name: STORAGE_KEYS.BUCKET_ITEMS,
      partialize: (state) => ({
        items: state.items,
        landmarks: state.landmarks,
      }),
    },
  ),
);
